import { Auth } from "aws-amplify";
import classNames from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import isEmail from "validator/lib/isEmail";
import OTPInput from "react-otp-input";
import { useNavigate } from "react-router-dom";
import {
  getUserAuthenticationHookDetails,
  verifySecurityCode,
} from "../../services/AuthService";
import { setUserDataForAPI } from "../../services/AxiosInterceptor";
import BackendService from "../../services/BackendService";
import { setUserPreferenceBeforeSignin } from "../../services/UserService";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import Icons from "../../utils/components/icons";
import "./Auth.scss";
import LeftPanel from "./LeftPanel";

const initialFormState = {
  username: "",
  password: "",
  email: "",
  authCode: "",
  phone: "",
};

export function SignIn() {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const [isOnline, setIsOnline] = useState(true);

  let kLogin = localStorage.getItem("keepmelogin");
  kLogin = kLogin && kLogin === "true" ? true : false;
  const [formState, updateFormState] = useState(initialFormState);
  const [userSigninData, setUserSigninData] = useState({});
  const [viewPass, setViewPass] = useState(true);
  const [credVerifyError, setCredVerifyError] = useState(false);
  const [credVerifyErrorMessage, setCredVerifyErrorMessage] = useState(false);
  const [otpVerifyError, setOTPVerifyError] = useState(false);
  const [otpVerifyErrorMessage, setOTPVerifyErrorMessage] = useState("");
  const [nextDisable, setNextDisable] = useState(true);
  const [otp, setOTP] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [resendMessage, setResendMessage] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const [steps, setSteps] = useState(0);
  const [keepMeLogin, setKeepMeLogin] = useState(kLogin);
  const [validation, setValidation] = useState({
    usernameInvalid: false,
    passwordEmpty: false,
    credentialInvalid: false,
  });
  const [otpMode, setOTPMode] = useState();
  const [otpModeGlobal, setOTPModeGlobal] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.addEventListener("online", () => setIsOnline(true));
    window.addEventListener("offline", () => setIsOnline(false));
  }, []);

  const viewPassword = useCallback(() => {
    setViewPass((prevVal) => !prevVal);
  }, []);

  const keepmeLogin = useCallback((event) => {
    setKeepMeLogin(event.target.checked);
  }, []);

  const moveStepsBack = useCallback(
    async (currentStep) => {
      if (currentStep > 0) {
        setSteps(currentStep - 1);
        setNextDisable(false);

        updateFormState(() => ({
          ...initialFormState,
        }));

        if (otpModeGlobal) {
          //reset the security code prefernce to original value
          const queryParam = {
            cognitoId: userSigninData?.username,
            attributeName: "SecuritySettings",
            keyName: "ReceiveSecurityCodeOn",
            keyValue: otpModeGlobal,
          };
          setUserPreferenceBeforeSignin(queryParam, userSigninData?.username);
        }
      }
    },
    [otpModeGlobal, userSigninData?.username]
  );

  const signInWithPhone = useCallback(
    async (phone, password) => {
      try {
        const user = await Auth.signIn(phone, password);
        setUserSigninData(user);
        const otpEnabledMode =
          user?.attributes?.["custom:ReceiveOTPOn"] === "Email"
            ? "Email"
            : user?.challengeName === "SMS_MFA"
            ? "Mobile"
            : "";

        if (otpEnabledMode) {
          setOTPMode(otpEnabledMode);
          setOTPModeGlobal(otpEnabledMode);
          setOTP("");
          setOTPVerifyError(false);
          setResendMessage(false);
          setCredVerifyError(false);
          setValidation({ ...validation, credentialInvalid: false });
          setSteps(steps + 1);
          setNextDisable(true);
          setTimeout(() => {
            let input = document.querySelectorAll(".otpInput input");
            input[0].focus();
          }, 200);
          setIsSubmitted(false);
          setIsLoading(false);
          setCodeSent(true);
          setTimeout(() => {
            setCodeSent(false);
          }, 3000);
        } else {
          setUserDataForAPI(user);
          sessionStorage.setItem("keepmelogin", "signin");
          localStorage.setItem("keepmelogin", keepMeLogin);
          navigate("/");
        }
      } catch (error) {
        // console.log("error signing in", error);
        const { code } = error;
        setIsSubmitted(false);
        setIsLoading(false);
        if (
          error.code === "NotAuthorizedException" &&
          error.message === "User is disabled."
        ) {
          updateFormState(() => ({ ...formState, username: "", password: "" }));
          setValidation({ ...validation, credentialInvalid: true });
          setCredVerifyErrorMessage(
            "Your Chorus account is currently disabled. Please contact Institution Admin"
          );
          return;
        }
        if (
          code === "NotAuthorizedException" ||
          code === "UserNotConfirmedException"
        ) {
          updateFormState(() => ({ ...formState, username: "", password: "" }));
          setValidation({ ...validation, credentialInvalid: true });
          setCredVerifyErrorMessage(
            "Incorrect Registered Email ID or Password"
          );
        }
      }
    },
    [formState, keepMeLogin, navigate, steps, validation]
  );

  const loginSuccess = useCallback(() => {
    // Set otp mode back to previous value
    try {
      const queryParam = {
        cognitoId: userSigninData?.username,
        attributeName: "SecuritySettings",
        keyName: "ReceiveSecurityCodeOn",
        keyValue: otpModeGlobal,
      };
      setUserPreferenceBeforeSignin(queryParam, userSigninData?.username);
    } catch (err) {
    } finally {
      sessionStorage.setItem("keepmelogin", "signin");
      localStorage.setItem("keepmelogin", keepMeLogin);
      navigate("/");
    }
  }, [keepMeLogin, navigate, otpModeGlobal, userSigninData?.username]);

  const verifyMobileOTP = useCallback(async () => {
    try {
      await Auth.confirmSignIn(userSigninData, otp, "SMS_MFA");
      loginSuccess();
    } catch (error) {
      setIsLoading(false);
      setOTP("");
      setNextDisable(true);

      if (!isOnline) {
        setOTPVerifyErrorMessage("No Internet Connection Detected");
        setOTPVerifyError(true);
        return;
      }

      const { code } = error;
      switch (code) {
        case "CodeMismatchException":
          setOTPVerifyErrorMessage("Incorrect Security Code");
          break;
        case "ExpiredCodeException":
          setOTPVerifyErrorMessage("Security code expired, please login again");
          break;
        case "LimitExceededException":
          setOTPVerifyErrorMessage(
            "Attempt limit exceeded, please try after some time."
          );
          break;
        default:
          setOTPVerifyErrorMessage("Something went wrong");
      }

      setOTPVerifyError(true);
    }
  }, [isOnline, loginSuccess, otp, userSigninData]);

  const verifyEmailOTP = useCallback(async () => {
    try {
      const { username } = formState;
      const queryParam = {
        attribute: "email",
        value: username,
        otp: otp,
      };
      await verifySecurityCode(queryParam);
      loginSuccess();
    } catch (error) {
      setIsLoading(false);
      setOTP("");
      setOTPVerifyError(true);
      const errMsg = error?.response?.data?.Message;

      if (
        error?.response?.data?.Message === "Failed to verify OTP. Incorrect OTP"
      ) {
        setOTPVerifyErrorMessage("Incorrect Email Security Code");
      } else {
        if (
          errMsg ===
          "Failed to verify OTP: OTP Expired. Regenerate OTP and try again"
        ) {
          setOTPVerifyErrorMessage("Security code expired, please login again");
        } else {
          setOTPVerifyErrorMessage("Something went wrong");
        }
      }
    }
  }, [formState, loginSuccess, otp]);

  const signInMFA = useCallback(async () => {
    setIsLoading(true);
    setOTPVerifyError(false);

    try {
      if (otpMode === "Mobile") {
        await verifyMobileOTP();
      } else {
        await verifyEmailOTP();
      }
    } catch (error) {
      console.error("Error during MFA sign-in:", error);
    }
  }, [otpMode, verifyMobileOTP, verifyEmailOTP]);

  const signIn = useCallback(async () => {
    setValidation({ ...validation, credentialInvalid: false });
    setCredVerifyError(false);
    const { username, password } = formState;

    if (!isEmail(username)) {
      setValidation({
        ...validation,
        usernameInvalid: false,
        credentialInvalid: true,
      });
      setCredVerifyErrorMessage("Incorrect Registered Email ID or Password");
      updateFormState(() => ({ ...formState, username: "", password: "" }));
      return false;
    }
    if (password.length === 0) {
      setValidation({
        ...validation,
        passwordEmpty: true,
        credentialInvalid: false,
      });
      updateFormState(() => ({ ...formState, username: "", password: "" }));
      return false;
    }
    setIsSubmitted(true);
    setIsLoading(true);
    const queryParam = {
      attribute: "email",
      attribute_value: username,
      action: "GetUserAttribute",
    };
    getUserAuthenticationHookDetails(queryParam)
      .then((response) => {
        if (response.data.Users.length) {
          const phoneAttribute = response.data.Users[0].Attributes.find(
            (elem) => elem.Name === "phone_number"
          );
          updateFormState({ ...formState, phone: phoneAttribute.Value });
          signInWithPhone(phoneAttribute.Value, password);
        } else {
          updateFormState(() => ({ ...formState, username: "", password: "" }));
          setCredVerifyError(true);
          setCredVerifyErrorMessage(
            "Incorrect Registered Email ID or Password"
          );
          setIsSubmitted(false);
          setIsLoading(false);
        }
      })
      .catch(function (error) {
        // console.log(error);
        updateFormState(() => ({ ...formState, username: "", password: "" }));
        setCredVerifyError(true);
        if (isOnline) {
          setCredVerifyErrorMessage(
            "Incorrect Registered Email ID or Password"
          );
        } else {
          setCredVerifyErrorMessage("No Internet Connection Detected");
        }
        setIsSubmitted(false);
        setIsLoading(false);
      });
  }, [formState, isOnline, signInWithPhone, validation]);

  const onChange = useCallback(
    (e) => {
      e.persist();
      setCredVerifyError(false);
      if (e.target.name === "username") {
        if (e.target.value.includes(" ")) {
          e.target.value = e.target.value.replace(/\s/g, "");
        }
        setValidation({
          ...validation,
          usernameInvalid: false,
          credentialInvalid: false,
        });
      }
      if (e.target.name === "password") {
        setValidation({
          ...validation,
          passwordEmpty: false,
          credentialInvalid: false,
        });
      }
      updateFormState((prevFormState) => ({
        ...prevFormState,
        [e.target.name]: e.target.value,
      }));
    },
    [validation]
  );

  const resendConfirmationCode = useCallback(
    async (showResendMsg = true) => {
      try {
        setIsLoading(true);
        setOTPVerifyError(false);
        const user = await Auth.signIn(formState.phone, formState.password);
        setUserSigninData(user);
        setUserDataForAPI(user); //For interceptor
        if (showResendMsg) {
          setResendMessage(true);
          setTimeout(() => {
            setResendMessage(false);
          }, 5000);
        }
      } catch (error) {
        setResendMessage(false);
        const { code } = error;
        switch (code) {
          case "LimitExceededException": {
            setOTPVerifyErrorMessage(
              "Attempt limit exceeded, please try after some time."
            );
            setOTPVerifyError(true);
            break;
          }
          default:
            setOTPVerifyErrorMessage("");
        }
      }
      setIsLoading(false);
    },
    [formState.password, formState.phone]
  );

  const onOTPChange = useCallback((val) => {
    setOTP(val);
    if (val.length < 6) {
      setNextDisable(true);
    } else {
      setNextDisable(false);
    }
    setOTPVerifyError(false);
  }, []);

  const getBackendConfig = useCallback(() => {
    const { email } = formState;
    BackendService.getConfig(email);
  }, [formState]);

  const handleKeypress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        if (steps === 0 && !nextDisable) signIn();
        if (steps === 1 && !nextDisable) signInMFA();
      }
    },
    [nextDisable, signIn, signInMFA, steps]
  );

  const updateSendCodeOnPreference = useCallback(
    async (currentOtpMode) => {
      const changeModeTo = currentOtpMode === "Mobile" ? "Email" : "Mobile";
      setIsLoading(true);
      try {
        const queryParam = {
          cognitoId: userSigninData?.username,
          attributeName: "SecuritySettings",
          keyName: "ReceiveSecurityCodeOn",
          keyValue: changeModeTo,
        };
        await setUserPreferenceBeforeSignin(
          queryParam,
          userSigninData?.username
        ); // update user pref for security code
        setOTPMode(changeModeTo);
        resendConfirmationCode(false);
        setCodeSent(true);
        setTimeout(() => {
          setCodeSent(false);
        }, 3000);
      } catch (err) {}
      setIsLoading(false);
    },
    [resendConfirmationCode, userSigninData?.username]
  );

  useEffect(() => {
    if (formState.username === "" || formState.password === "") {
      setNextDisable(true);
    } else {
      setNextDisable(false);
    }
  }, [formState]);

  return (
    <>
      <section className="authContainer">
        <LeftPanel />

        {/* Right Side Panel starts */}
        <div className="rightPanel">
          {/* Header Section Starts */}
          <>
            <img
              id="logo"
              src={require("../../assets/images/chorus-rgb.png")}
              className="img-fluid logo mb-4"
              alt="chorus"
            />
            <div
              className={classNames("container-fluid backBtnWrapper", {
                "d-none": steps !== 1,
              })}
            >
              <button
                onClick={() => moveStepsBack(steps)}
                id="back"
                className="linkBtn"
              >
                {"< Back"}
              </button>
            </div>
            <div
              className={classNames(
                "container-fluid d-flex flex-row justify-content-end mb-4 align-items-center new-user-heading",
                {
                  "d-none": steps !== 0,
                }
              )}
            >
              <div id="new-here-text">{t("newHere")}</div>
              <button
                // onClick={() => navigate("/signup")}
                disabled
                id="signup"
                name="signup"
                className="linkBtn register"
              >
                {t("register")}
              </button>
            </div>
          </>
          {/* Header Section Ends */}

          {/* Body Section Starts */}
          <>
            {/* Login Screen Starts */}
            <>
              <div
                className={classNames("container-fluid flex-fill", {
                  "d-none": steps !== 0,
                })}
                onKeyDown={handleKeypress}
              >
                <div className="fieldset mb-4">
                  <label id="email_label">{t("registeredEmailId")}</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={formState.username}
                      id="username"
                      placeholder={t("emailIdPlaceholder")}
                      aria-label="Username"
                      aria-describedby="basic-addon1"
                      name="username"
                      maxLength="64"
                      onChange={onChange}
                      onBlur={getBackendConfig}
                    />
                  </div>
                  {validation.usernameInvalid && (
                    <span id="email_error" className="validationError">
                      {t("enterValidEmailId")}
                    </span>
                  )}
                </div>
                <div className="fieldset mb-4">
                  <label id="pass_label">{t("password")}</label>
                  <div className="input-group">
                    <input
                      type={viewPass ? "password" : "text"}
                      className="form-control"
                      value={formState.password}
                      id="password"
                      placeholder={t("passwordPlaceholder")}
                      aria-label="Username"
                      aria-describedby="basic-addon1"
                      name="password"
                      maxLength="64"
                      onChange={onChange}
                    />
                    <span
                      className="input-group-text"
                      id="viewpass"
                      onClick={viewPassword}
                    >
                      {!viewPass && Icons.viewPass}
                      {viewPass && Icons.hidePass}
                    </span>
                  </div>
                  {validation.passwordEmpty && (
                    <span id="pass_error" className="validationError">
                      {t("enterPassErr")}
                    </span>
                  )}
                </div>
                <div className="fieldset d-flex flex-row justify-content-between align-items-center mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={keepMeLogin}
                      id="keepMeLogin"
                      onChange={keepmeLogin}
                    />
                    <label
                      id="keepMeLogin_label"
                      htmlFor="keepMeLogin"
                      className="fs-8 withCheckbox"
                    >
                      {t("keepMeLoggedIn")}
                    </label>
                  </div>
                  <button
                    onClick={() => navigate("/resetpassword")}
                    id="resetpass"
                    name="resetpass"
                    className="linkBtn"
                  >
                    {t("resetPass")}
                  </button>
                </div>
                <div className="fieldset mb-4">
                  {(validation.credentialInvalid || credVerifyError) && (
                    <span id="login_error" className="validationError">
                      {credVerifyErrorMessage}
                    </span>
                  )}
                </div>
                <div className="fieldset mb-4">
                  <button
                    type="submit"
                    id="login"
                    className={
                      nextDisable || isSubmitted
                        ? "submitBtn dark pointer"
                        : "submitBtn pointer"
                    }
                    disabled={nextDisable}
                    onClick={signIn}
                  >
                    {isSubmitted ? (
                      <span>
                        {t("loading")}
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        ></div>
                      </span>
                    ) : (
                      <span>{t("login")}</span>
                    )}
                  </button>
                </div>
              </div>
            </>
            {/* Login Screen Ends */}

            {/* Two FA Screen Starts */}
            <div
              className={classNames("container-fluid flex-fill", {
                "d-none": steps !== 1,
              })}
              onKeyDown={handleKeypress}
            >
              <div className="fieldset d-flex flex-column text-center justify-content-center mb-4 align-items-center">
                <p className="fs-6 mb-0 text-dark" id="2fa-text">
                  {t("twoFAHeading")}
                </p>
                <p className="fs-7" id="code-sent-text">
                  {otpMode === "Mobile"
                    ? t("twoFASubHeadingMobile")
                    : t("twoFASubHeadingEmail")}
                </p>
              </div>
              <div className="fieldset d-flex flex-column justify-content-center mb-4 otpInputWrapper">
                <label id="code_label">{t("securityCodeHeading")}</label>
                <br />
                <OTPInput
                  className="otpInput"
                  value={otp}
                  focusStyle={{
                    border: "1px solid #CFD3DB",
                    outline: "none",
                  }}
                  onChange={(val) => {
                    onOTPChange(val);
                  }}
                  separator={<span style={{ width: "8px" }}> </span>}
                  shouldAutoFocus={true}
                  numInputs={6}
                  isInputNum={true}
                  isDisabled={false}
                  isInputSecure={true}
                />
                {otpVerifyError && (
                  <span id="code_error" className="validationError">
                    {otpVerifyErrorMessage}
                  </span>
                )}
              </div>
              {resendMessage && (
                <div className="fieldset mb-4">
                  <p
                    className="fs-7 mb-0 successMsg text-center"
                    id="code-resent-text"
                  >
                    {otpMode === "Mobile"
                      ? t("securitycodeResentOnMobile")
                      : t("securitycodeResentOnEmail")}
                  </p>
                </div>
              )}
              {codeSent && (
                <div className="fieldset mb-4 codeSent">
                  <p
                    className="fs-7 mb-0 successMsg text-center"
                    id="code-resent-text"
                  >
                    {otpMode === "Mobile"
                      ? t("codeSentOnMobile")
                      : t("codeSentOnEmail")}
                  </p>
                </div>
              )}
              <div className="fieldset d-flex flex-row justify-content-end align-items-center mb-4">
                <button
                  onClick={() => resendConfirmationCode(true)}
                  id="resendcode"
                  name="resendcode"
                  className="linkBtn"
                >
                  {t("resendCode")}
                </button>
              </div>
              <div className="fieldset mb-4">
                <button
                  type="submit"
                  id="verify"
                  className={nextDisable ? "submitBtn dark" : "submitBtn"}
                  disabled={nextDisable}
                  onClick={signInMFA}
                >
                  {t("verify")}
                </button>
              </div>
              <div className="opt-mode-change-container">
                <p className="text-h2">{t("or")} </p>
                <p className="text-h2 black code-not-received-text">
                  {t("codeNotReceived")}{" "}
                </p>
                <p
                  className="heading-h3 send-on"
                  onClick={() => updateSendCodeOnPreference(otpMode)}
                >
                  {otpMode === "Mobile" ? t("sendOnEmail") : t("sendOnMobile")}
                </p>
              </div>
            </div>
            {/* Two FA Screen Ends */}
          </>
          {/* Body Section Ends */}

          {/* Footer Section Starts */}
          <div className="fieldset text-center" id="copyright-text">
            {t("rightReservedText")}
          </div>
          {/* Footer Section Ends */}
        </div>
        {/* Right Side Panel ends */}
        {isLoading && <LoadingSpinner />}
      </section>
    </>
  );
}

export default SignIn;
